import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { completeLogin } from '../auth/oidc';
import { getConfig } from '../config';
import { Logo } from '../components/Logo';

/**
 * The sign-in screen leans on the guideline's two strongest devices: MC Black
 * with the 20° stripe behind, and the wordmark on white in the foreground. The
 * card is deliberately white in both themes because the supplied logo artwork
 * is the black lockup and is used unaltered.
 */
export function LoginPage() {
  const { signIn, resetPassword } = useAuth();
  return (
    <div className="login">
      <div className="login-card">
        <Logo className="login-logo" />
        <h1>Brands CMS</h1>
        <p>Manage brands, models, variants and colours for the Motorcity app.</p>
        <button type="button" className="btn primary wide" onClick={() => signIn('/brands')}>
          Sign in
        </button>
        <button type="button" className="btn subtle wide login-reset" onClick={resetPassword}>
          Forgot your password?
        </button>
        <p className="login-note">
          Passwords need <strong>12+ characters</strong> with an uppercase and lowercase letter, a
          number and a symbol. Access is invite-only — ask an administrator if you do not have an
          account.
        </p>
      </div>
    </div>
  );
}

/**
 * Handles the Hosted UI redirect.
 *
 * The exchange runs at most once per mount, guarded by a ref rather than by
 * effect dependencies. An authorization code is single-use and the PKCE state
 * and verifier are consumed on first use, so a second attempt cannot succeed —
 * it can only report a mismatch for a sign-in that already worked. StrictMode's
 * double-invoked effects in development hit the same path.
 */
export function CallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { onTokens } = useAuth();
  const [error, setError] = useState<string | undefined>();
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const oauthError = params.get('error_description') ?? params.get('error');
    if (oauthError) {
      setError(oauthError);
      return;
    }

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      setError('Sign-in response was incomplete. Please try again.');
      return;
    }

    completeLogin(getConfig(), code, state)
      .then(({ tokens, returnTo }) => {
        onTokens(tokens);
        navigate(returnTo === '/' ? '/brands' : returnTo, { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in failed');
      });
  }, [params, navigate, onTokens]);

  return (
    <div className="login">
      <div className="login-card">
        <Logo className="login-logo" />
        {error ? (
          <>
            <h1>Sign-in failed</h1>
            <p className="field-error">{error}</p>
            <button
              type="button"
              className="btn wide"
              onClick={() => navigate('/', { replace: true })}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="loading">Signing you in…</p>
        )}
      </div>
    </div>
  );
}

export function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="login">
      <div className="login-card">
        <Logo className="login-logo" />
        <h1>Page not found</h1>
        <p>That page does not exist, or you no longer have access to it.</p>
        <button type="button" className="btn primary wide" onClick={() => navigate('/brands')}>
          Go to brands
        </button>
      </div>
    </div>
  );
}
